import { useState, useEffect, useCallback } from 'react';
import { type Theme, loadTheme, saveTheme, resolveTheme } from '../lib/theme.js';

// Every useTheme() instance (header toggle, Settings page) holds its own state;
// broadcast changes so they stay in sync (the shell applies `resolved` as a class).
const THEME_EVENT = 'tsc-theme-change';

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(loadTheme);
  const resolved = resolveTheme(theme);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    saveTheme(next);
    window.dispatchEvent(new CustomEvent<Theme>(THEME_EVENT, { detail: next }));
  }, []);

  const toggle = useCallback(() => {
    setTheme(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setTheme]);

  useEffect(() => {
    function onChange(e: Event) {
      const next = (e as CustomEvent<Theme>).detail;
      if (next) setThemeState(next);
    }
    window.addEventListener(THEME_EVENT, onChange);
    return () => window.removeEventListener(THEME_EVENT, onChange);
  }, []);

  useEffect(() => {
    saveTheme(theme);
  }, [theme]);

  return { theme, resolved, setTheme, toggle };
}
